/**
 * ReportCanvas Component (Stories 5.4, 5.6, 5.39)
 *
 * react-grid-layout powered 12-column grid for report widget placement.
 * A4-proportioned container with page break markers at row boundaries.
 *
 * Story 5.39: Migrated from CSS Grid + @dnd-kit to react-grid-layout.
 * RGL handles drag-and-drop, resize, compaction, and collision detection.
 *
 * Responsive behavior:
 * - Mobile (<768px): Stack all widgets to colSpan=12 (single column)
 * - Tablet (768-1024px): Cap at colSpan=6 (2-column max)
 * - Desktop (>1024px): Full 12-column grid with all valid spans
 */

import { useRef, useMemo, useCallback, useEffect } from "react";
import { MousePointerClick, Image as ImageIcon, Hash, TrendingUp, BarChart3, Table, PieChart, Newspaper } from "lucide-react";
import { WidgetRenderer } from "./widgets/WidgetRenderer";
import { CanvasGrid } from "./CanvasGrid";
import { useViewportBreakpoint, type ViewportBreakpoint } from "../../lib/useViewportBreakpoint";
import type { WidgetConfig } from "../../lib/templateUtils";
import type { WidgetData } from "../../lib/widgetData";
import {
  calculatePageBreaks,
  GRID_COLUMNS,
  type ValidColSpan,
} from "../../lib/gridUtils";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import type { ReportFormat } from "@provenleap/types/report-rendering";
import { useReportDnd } from "./ReportDndProvider";
import { useCanvasDropTarget } from "./useCanvasDropTarget";
import { applyMoveDelta, type GridMetrics } from "../../lib/reportDnd";

// ── Responsive Grid Helpers ─────────────────────────────────────────

function getResponsiveWidgets(
  widgets: WidgetConfig[],
  breakpoint: ViewportBreakpoint
): WidgetConfig[] {
  if (breakpoint === "desktop") return widgets;

  if (breakpoint === "mobile") {
    return widgets.map((w, i) => ({
      ...w,
      position: { ...w.position, row: i, col: 0, colSpan: 12 },
    }));
  }

  let currentRow = 0;
  let currentCol = 0;
  return widgets.map((w) => {
    const cappedSpan = Math.min(w.position.colSpan, 6);
    if (currentCol + cappedSpan > 12) {
      currentRow++;
      currentCol = 0;
    }
    const adapted = {
      ...w,
      position: {
        ...w.position,
        row: currentRow,
        col: currentCol,
        colSpan: cappedSpan,
      },
    };
    currentCol += cappedSpan;
    return adapted;
  });
}

// ── Grid Guides ─────────────────────────────────────────────────────

function GridGuides({ gapPx }: { gapPx: number }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-0" style={{ padding: "24px" }}>
      <div className="relative w-full h-full" style={{ display: "grid", gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`, gap: gapPx }}>
        {Array.from({ length: GRID_COLUMNS - 1 }, (_, i) => (
          <div
            key={`guide-${i}`}
            data-testid={`grid-guide-${i}`}
            className="border-r border-dashed border-muted-foreground/10 h-full"
          />
        ))}
        <div />
      </div>
    </div>
  );
}

// ── Empty-state ghost skeleton layout ───────────────────────────────
// Shows faded placeholders of a typical report (cover, KPI row, two charts).
// Clicking a ghost adds that single widget at its grid position.

interface GhostItem {
  type: string;
  label: string;
  icon: typeof Hash;
  position: { row: number; col: number; colSpan: number; rowSpan: number };
  /** grid column span out of 12, for the CSS-grid preview */
  span: number;
  /** preview height in px */
  h: number;
  /** opacity, fading further down the page (top rows most visible) */
  opacity: number;
}

const GHOST_ITEMS: GhostItem[] = [
  // Top — most visible
  { type: "cover-page", label: "Cover page", icon: ImageIcon, position: { row: 0, col: 0, colSpan: 12, rowSpan: 3 }, span: 12, h: 96, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 0, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 3, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 6, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "kpi", label: "KPI", icon: Hash, position: { row: 3, col: 9, colSpan: 3, rowSpan: 1 }, span: 3, h: 64, opacity: 1 },
  { type: "line-chart", label: "Line chart", icon: TrendingUp, position: { row: 4, col: 0, colSpan: 6, rowSpan: 2 }, span: 6, h: 140, opacity: 1 },
  { type: "bar-chart", label: "Bar chart", icon: BarChart3, position: { row: 4, col: 6, colSpan: 6, rowSpan: 2 }, span: 6, h: 140, opacity: 1 },
  { type: "data-table", label: "Data table", icon: Table, position: { row: 6, col: 0, colSpan: 12, rowSpan: 3 }, span: 12, h: 130, opacity: 1 },
  { type: "pie-chart", label: "Pie chart", icon: PieChart, position: { row: 9, col: 0, colSpan: 6, rowSpan: 2 }, span: 6, h: 120, opacity: 1 },
  { type: "top-posts", label: "Top posts", icon: Newspaper, position: { row: 9, col: 6, colSpan: 6, rowSpan: 2 }, span: 6, h: 120, opacity: 1 },
];

function EmptyGhostLayout({
  onEmptyAddAt,
  onEmptyQuickAdd,
  onEmptyDrop,
}: {
  onEmptyAddAt?: ReportCanvasProps["onEmptyAddAt"];
  onEmptyQuickAdd?: ReportCanvasProps["onEmptyQuickAdd"];
  onEmptyDrop?: ReportCanvasProps["onEmptyDrop"];
}) {
  const handleClick = (item: GhostItem) => {
    if (onEmptyAddAt) {
      onEmptyAddAt(item.type, item.position);
    } else if (onEmptyQuickAdd && (item.type === "kpi" || item.type === "line-chart")) {
      onEmptyQuickAdd(item.type);
    }
  };

  return (
    <div
      data-testid="report-canvas-empty"
      className="flex flex-col gap-3"
      // Empty canvas has no RGL grid, so wire HTML5 drop here directly:
      // dragOver must preventDefault for the browser to accept the drop.
      onDragOver={onEmptyDrop ? (e) => e.preventDefault() : undefined}
      onDrop={
        onEmptyDrop
          ? (e) => {
              e.preventDefault();
              onEmptyDrop(e.nativeEvent);
            }
          : undefined
      }
    >
      <div className="mb-4 text-center text-[17px]" style={{ color: "hsl(var(--foreground))" }}>
        Click a placeholder to add it, or drag widgets from the palette, or press{" "}
        <kbd className="text-sm font-medium text-foreground border border-input rounded-[9px] px-2.5 py-1 bg-muted/40 ml-2">
          <span className="relative top-px text-base">⌘</span> + K
        </kbd>
      </div>
      <div className="grid grid-cols-12 gap-3">
        {GHOST_ITEMS.map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={`${item.type}-${i}`}
              type="button"
              onClick={() => handleClick(item)}
              data-testid={`ghost-${item.type}-${i}`}
              aria-label={`Add ${item.label}`}
              className="group/ghost flex flex-col items-center justify-center gap-1.5 rounded-[14px] bg-muted/10 hover:!opacity-100 hover:text-foreground hover:bg-muted/40 transition-all cursor-pointer"
              style={{
                gridColumn: `span ${item.span} / span ${item.span}`,
                height: item.h,
                border: "1px dashed hsl(214 25% 62%)",
                color: "hsl(var(--foreground))",
                opacity: item.opacity,
              }}
            >
              <Icon size={17} className="shrink-0" />
              <span className="text-[17px] text-center">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────

export interface ReportCanvasProps {
  widgets: WidgetConfig[];
  widgetData?: Record<string, WidgetData>;
  selectedWidgetId?: string | null;
  onWidgetSelect?: (widgetId: string) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onWidgetDuplicate?: (widgetId: string) => void;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
  editable?: boolean;
  /** Cover page: date range start for period display */
  reportDateRangeStart?: string;
  /** Cover page: date range end for period display */
  reportDateRangeEnd?: string;
  /** Cover page: client name (derived, not stored in config) */
  selectedClientName?: string;
  /** Cover page: client logo URL from client profile (auto-pull) */
  selectedClientLogoUrl?: string;
  /** Report format — controls canvas dimensions (Story 5.23) */
  format?: ReportFormat;
  /** Commit a widget MOVE to a new grid cell (dnd-kit, Epic 37). */
  onWidgetMove?: (widgetId: string, cell: { row: number; col: number }) => void;
  /** Commit a widget RESIZE (colSpan snapped to valid, rowSpan free). */
  onWidgetResize?: (widgetId: string, colSpan: number, rowSpan: number) => void;
  /** Canvas zoom level as percentage (Story 5.38) */
  zoomLevel?: number;
  /** Empty-state quick-add: called when user clicks one of the quick action buttons in the empty canvas */
  onEmptyQuickAdd?: (type: "kpi" | "line-chart" | "data-table" | "heading") => void;
  /**
   * Empty-state ghost skeleton: called when the user clicks a ghost placeholder.
   * Adds that single widget at the given grid position.
   */
  onEmptyAddAt?: (
    type: string,
    position: { row: number; col: number; colSpan: number; rowSpan: number },
  ) => void;
  /**
   * Empty-state drop: called when a palette widget is DROPPED onto the empty
   * canvas (which has no RGL grid). Receives the raw DragEvent; the parent
   * parses dataTransfer and adds the widget (same path as the grid onDrop).
   */
  onEmptyDrop?: (e: DragEvent) => void;
}

export function ReportCanvas({
  widgets,
  widgetData,
  selectedWidgetId,
  onWidgetSelect,
  onWidgetDelete,
  onWidgetDuplicate,
  onAutoResize,
  editable = false,
  reportDateRangeStart,
  reportDateRangeEnd,
  selectedClientName,
  selectedClientLogoUrl,
  format = "document",
  onWidgetMove,
  onWidgetResize,
  zoomLevel = 100,
  onEmptyQuickAdd,
  onEmptyAddAt,
  onEmptyDrop,
}: ReportCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const breakpoint = useViewportBreakpoint();
  const isDesktop = breakpoint === "desktop";
  const effectiveEditable = editable && isDesktop;

  // Format-aware grid dimensions (Story 5.23)
  const dims = useMemo(() => getGridDimensions(format), [format]);
  const contentWidthPx = dims.contentWidthPx;
  const contentHeightPx = dims.contentHeightPx;
  const rowHeightPx = dims.rowHeightPx;
  const gapPx = dims.gapPx;

  // Outer padding (simulates the printable margins around the PDF content area)
  const PAPER_PADDING_PX = 48;
  const paperWidthPx = contentWidthPx + PAPER_PADDING_PX * 2;
  const paperMinHeightPx = contentHeightPx + PAPER_PADDING_PX * 2;

  const responsiveWidgets = useMemo(
    () => getResponsiveWidgets(widgets, breakpoint),
    [widgets, breakpoint]
  );

  const pageBreaks = useMemo(
    () => isDesktop ? calculatePageBreaks(responsiveWidgets, dims.contentHeightPx, rowHeightPx, gapPx) : [],
    [responsiveWidgets, isDesktop, dims.contentHeightPx, rowHeightPx, gapPx]
  );

  const pageBreakPositions = useMemo(() => {
    return pageBreaks.map((breakRow) => {
      // Calculate top position accounting for row heights and gaps
      // RGL uses margin (gap) between items, so top = breakRow * (rowHeight + gap)
      const topPx = breakRow * (rowHeightPx + gapPx);
      return { row: breakRow, topPx };
    });
  }, [pageBreaks, rowHeightPx, gapPx]);

  // Canvas background click — deselect (only when clicking empty area)
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    // Only deselect if clicking the canvas wrapper itself, not a child widget
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest("[data-testid='report-canvas-grid']") === e.target) {
      onWidgetSelect?.("");
    }
  }, [onWidgetSelect]);

  const canvasPadding = 24; // inner grid padding (matches the printable margins)
  const scale = zoomLevel / 100;

  // Shared grid metrics for all positioned-grid math (placeholder, move, resize).
  const gridMetrics = useMemo<GridMetrics>(
    () => ({ contentWidthPx, rowHeightPx, gapPx, paddingPx: canvasPadding }),
    [contentWidthPx, rowHeightPx, gapPx],
  );

  // ── dnd-kit canvas DnD (Epic 37 — fully replaces react-grid-layout) ─────────
  const { activeDrag, registerHoverCellGetter, registerMoveHandler } = useReportDnd();
  const activeSpan = activeDrag ? { colSpan: activeDrag.colSpan, rowSpan: activeDrag.rowSpan } : null;
  const dropTarget = useCanvasDropTarget({
    enabled: effectiveEditable,
    scale,
    format: format === "slide-deck" ? "slide-deck" : "document",
    activeSpan,
    paddingPx: 24, // matches canvasPadding / RGL containerPadding
  });
  // Register the hover-cell getter so the provider can read the target cell on drop.
  useEffect(() => {
    registerHoverCellGetter(dropTarget.getHoverCell);
  }, [registerHoverCellGetter, dropTarget.getHoverCell]);

  // Widget MOVE: the provider routes (widgetId, screen-delta) here on drag-end; we
  // own the widget's current cell + metrics, so we snap the delta to a cell and
  // commit via onWidgetMove. Read widgets from a ref so the handler identity stays
  // stable (registered once) yet always sees the latest positions.
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  const handleWidgetMoveDelta = useCallback(
    (widgetId: string, delta: { x: number; y: number }) => {
      const w = widgetsRef.current.find((x) => x.id === widgetId);
      if (!w || w.type === "cover-page") return; // cover-page is pinned
      const cell = applyMoveDelta(
        { row: w.position.row, col: w.position.col },
        delta,
        scale,
        gridMetrics,
        { colSpan: w.position.colSpan, rowSpan: w.position.rowSpan ?? 1 },
      );
      if (cell.row !== w.position.row || cell.col !== w.position.col) {
        onWidgetMove?.(widgetId, cell);
      }
    },
    [scale, gridMetrics, onWidgetMove],
  );
  useEffect(() => {
    registerMoveHandler(handleWidgetMoveDelta);
  }, [registerMoveHandler, handleWidgetMoveDelta]);

  const handleResizeCommit = useCallback(
    (widgetId: string, colSpan: ValidColSpan, rowSpan: number) => {
      onWidgetResize?.(widgetId, colSpan, rowSpan);
    },
    [onWidgetResize],
  );

  // Empty canvas state
  if (widgets.length === 0) {
    return (
      <div
        data-testid="report-canvas-wrapper"
        className="report-surface relative mx-auto rounded-[22px] bg-background shadow-xs"
        style={{
          width: isDesktop ? paperWidthPx : undefined,
          minHeight: isDesktop ? paperMinHeightPx : undefined,
          padding: isDesktop ? PAPER_PADDING_PX : undefined,
          border: "0.9px solid var(--hairline)",
        }}
      >
        <div
          ref={effectiveEditable ? dropTarget.setNodeRef : canvasRef}
          data-testid="report-canvas-grid"
          className="min-h-[320px]"
          aria-label="Report canvas drop zone"
          style={effectiveEditable ? { position: "relative" } : undefined}
        >
          {effectiveEditable ? (
            <EmptyGhostLayout onEmptyAddAt={onEmptyAddAt} onEmptyQuickAdd={onEmptyQuickAdd} onEmptyDrop={onEmptyDrop} />
          ) : (
            <div
              data-testid="report-canvas-empty"
              className="flex flex-col items-center justify-center py-24 text-muted-foreground"
            >
              <MousePointerClick size={32} className="mb-3 text-muted-foreground/40" />
              <p className="text-center text-base">This report has no widgets yet</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="report-canvas-wrapper"
      className="report-surface relative mx-auto rounded-[22px] bg-background shadow-xs"
      style={{
        width: isDesktop ? paperWidthPx : undefined,
        minHeight: isDesktop ? paperMinHeightPx : undefined,
        padding: isDesktop ? PAPER_PADDING_PX : undefined,
        border: "0.9px solid var(--hairline)",
      }}
      onClick={handleCanvasClick}
    >
      {/* Mobile/tablet: read-only message */}
      {!isDesktop && editable && (
        <div
          data-testid="edit-on-desktop-banner"
          className="mb-3 rounded-lg bg-muted px-4 py-2 text-sm text-muted-foreground text-center"
        >
          Edit layout on desktop for the full editing experience.
        </div>
      )}

      {/* Page break markers — edit mode only */}
      {isDesktop && editable && pageBreakPositions.map(({ row, topPx }, index) => (
        <div
          key={`break-${row}`}
          data-testid={`page-break-${index + 2}`}
          className="absolute left-0 right-0 flex items-center pointer-events-none z-10"
          style={{ top: topPx + canvasPadding }}
        >
          <div className="flex-1 border-t-2 border-dashed border-red-300" />
          <span className="mx-2 text-xs text-red-400 font-medium whitespace-nowrap">
            Page {index + 2}
          </span>
          <div className="flex-1 border-t-2 border-dashed border-red-300" />
        </div>
      ))}

      {/* Grid guides in edit mode */}
      {effectiveEditable && <GridGuides gapPx={gapPx} />}

      {/* dnd-kit canvas droppable wrapping the positioned CanvasGrid (Epic 37 —
          fully replaces react-grid-layout). The wrapper fills the paper's content
          area (minHeight) and IS the drop target (setNodeRef). A palette drag
          tracks the cursor → grid cell (transform-safe) and renders the snap
          placeholder below; on drop the widget lands at that cell. CanvasGrid
          positions each widget absolutely and owns move + resize. */}
      <div
        ref={effectiveEditable ? dropTarget.setNodeRef : undefined}
        data-testid="report-canvas-grid"
        aria-label="Report canvas drop zone"
        style={effectiveEditable ? { position: "relative", minHeight: contentHeightPx } : undefined}
      >
        <CanvasGrid
          widgets={responsiveWidgets}
          metrics={gridMetrics}
          minHeightPx={contentHeightPx}
          scale={scale}
          editable={effectiveEditable}
          selectedWidgetId={selectedWidgetId}
          movingWidgetId={null}
          onWidgetResize={handleResizeCommit}
          onWidgetSelect={onWidgetSelect}
          onWidgetDelete={onWidgetDelete}
          onWidgetDuplicate={onWidgetDuplicate}
          renderWidget={(widget) => (
            <WidgetRenderer
              widget={widget}
              widgetData={widgetData}
              onAutoResize={onAutoResize}
              reportDateRangeStart={reportDateRangeStart}
              reportDateRangeEnd={reportDateRangeEnd}
              selectedClientName={selectedClientName}
              selectedClientLogoUrl={selectedClientLogoUrl}
              format={format}
            />
          )}
        />
      </div>
    </div>
  );
}
